import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type { User } from "@shared/schema";
import { isPostHogEnabled } from "@/lib/posthog";
import { apiFetch, AUTH_USER_QUERY_KEY } from "@/lib/queryClient";

// CSR-2309 — deliberately the one caller that does NOT go through `apiFetch`:
// this probe decides whether a session exists, so a 401 is its answer rather
// than a failure, and the seam retracts this very query.
async function fetchUser(): Promise<User | null> {
  const response = await fetch("/api/auth/user", {
    credentials: "include",
  });

  if (response.status === 401) {
    return null;
  }

  if (response.status === 403) {
    const data = await response.json();
    throw new Error(data.message || "Access denied");
  }

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response.json();
}

export function useAuth() {
  const queryClient = useQueryClient();
  const ph = usePostHog();
  const phEnabled = isPostHogEnabled();
  const identifiedUserId = useRef<string | null>(null);

  const { data: user, isLoading, error } = useQuery<User | null>({
    queryKey: AUTH_USER_QUERY_KEY,
    queryFn: fetchUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (!phEnabled || !ph) return;
    if (user && user.id !== identifiedUserId.current) {
      ph.identify(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      });
      identifiedUserId.current = user.id;
    }
  }, [user, ph, phEnabled]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    error: error as Error | null,
  };
}

export function useUpdateUserSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: { crmAccess: boolean }) => {
      const response = await apiFetch("/api/auth/user/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (!response.ok) throw new Error("Failed to update settings");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_USER_QUERY_KEY });
    },
  });
}
